import { useRef, useState } from 'react';
import { postImport } from '../../lib/api.js';
import { Toast } from '../Toast.js';
import type { ImportMode, ImportResult } from '../../lib/types.js';

type ConfirmState = { file: File; text: string };

export function DataIO(): JSX.Element {
  const fileRef = useRef<HTMLInputElement>(null);
  const [confirming, setConfirming] = useState<ConfirmState | null>(null);
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const handlePickFile = () => fileRef.current?.click();

  const handleFile = async (f: File | null) => {
    if (!f) return;
    try {
      const text = await f.text();
      // Parse early so we can fail fast before opening the modal.
      JSON.parse(text);
      setConfirming({ file: f, text });
    } catch {
      setToast('文件不是有效 JSON');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const runImport = async (mode: ImportMode) => {
    if (!confirming) return;
    setImporting(true);
    try {
      const payload: unknown = JSON.parse(confirming.text);
      const result = await postImport(payload, mode);
      setToast(formatImportSummary(result));
      setConfirming(null);
    } catch (e) {
      setToast(`导入失败 · ${(e as Error).message ?? ''}`.trim());
    } finally {
      setImporting(false);
    }
  };

  return (
    <section aria-label="数据备份">
      <h2 className="text-secondary text-hint mb-2">数据备份</h2>
      <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-3">
        <a
          href="/api/export"
          download
          className="inline-flex h-11 items-center justify-center rounded-full border border-border text-primary text-sentence active:bg-surface-2 px-5"
        >
          导出全部数据
        </a>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
        />
        <button
          type="button"
          onClick={handlePickFile}
          className="h-11 rounded-full border border-border text-primary text-sentence active:bg-surface-2 px-5"
        >
          导入
        </button>
        <p className="text-muted text-hint">
          导入会要求选择「合并」或「覆盖」。导出文件可在新设备恢复。
        </p>
      </div>

      {confirming ? (
        <ConfirmModal
          fileName={confirming.file.name}
          busy={importing}
          onCancel={() => (importing ? null : setConfirming(null))}
          onPick={(m) => void runImport(m)}
        />
      ) : null}

      {toast ? <Toast message={toast} onDone={() => setToast(null)} durationMs={2400} /> : null}
    </section>
  );
}

type ModalProps = {
  fileName: string;
  busy: boolean;
  onCancel: () => void;
  onPick: (mode: ImportMode) => void;
};

function ConfirmModal({ fileName, busy, onCancel, onPick }: ModalProps): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="导入模式"
      className="fixed inset-0 z-40 flex items-center justify-center px-6"
    >
      <div className="absolute inset-0 bg-canvas/75" onClick={onCancel} aria-hidden />
      <div className="relative w-full max-w-card bg-surface border border-border rounded-2xl p-5 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-primary text-sentence">导入数据</h3>
          <p className="text-secondary text-hint break-all">{fileName}</p>
        </div>
        <p className="text-secondary text-hint">
          合并：与现有数据合并，重复词条以较高熟悉度为准。覆盖：先清空所有用户数据再导入。
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => onPick('merge')}
            className="h-11 rounded-full bg-primary text-canvas text-sentence active:opacity-80 disabled:opacity-50"
          >
            {busy ? '导入中...' : '合并'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onPick('replace')}
            className="h-11 rounded-full border border-border text-primary text-sentence active:bg-surface-2 disabled:opacity-50"
          >
            覆盖
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="h-10 text-secondary text-hint active:text-primary disabled:opacity-50"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
}

function formatImportSummary(r: ImportResult): string {
  const parts = [
    `词条 ${r.imported.personal_words}`,
    `偏好 ${r.imported.user_prefs}`,
  ];
  if (r.imported.word_encounters > 0) parts.push(`遇见 ${r.imported.word_encounters}`);
  if (r.skipped > 0) parts.push(`跳过 ${r.skipped}`);
  return `导入完成 · ${parts.join(' · ')}`;
}
